import { Task } from '@app/shared/types/models/task/task.model';

/**
 * Walks the reactive parent chain from the given task upward.
 * Returns ancestor IDs from the immediate parent to the root-most ancestor.
 * Stops on missing parents or cyclic parent relationships.
 */
export function collectTaskAncestorIds(task: Task | null | undefined): string[] {
  const ancestorIds: string[] = [];
  const visited = new Set<string>();
  let currentParent = task?.parentTask?.();

  while (currentParent?.id) {
    if (visited.has(currentParent.id)) {
      break;
    }

    visited.add(currentParent.id);
    ancestorIds.push(currentParent.id);
    currentParent = currentParent.parentTask?.();
  }

  return ancestorIds;
}

/**
 * Returns ancestor IDs that should be auto-expanded so the active task becomes visible,
 * but only when the root-most ancestor is present on the currently rendered root-task page.
 */
export function resolveAutoExpandedAncestorIds(
  activeTask: Task | null | undefined,
  rootTaskIdsOnPage: Iterable<string>
): Set<string> {
  if (!activeTask?.id) {
    return new Set<string>();
  }

  const ancestorIds = collectTaskAncestorIds(activeTask);
  if (ancestorIds.length === 0) {
    return new Set<string>();
  }

  const rootIds = rootTaskIdsOnPage instanceof Set ? rootTaskIdsOnPage : new Set(rootTaskIdsOnPage);
  const rootMostAncestorId = ancestorIds[ancestorIds.length - 1];
  if (!rootIds.has(rootMostAncestorId)) {
    return new Set<string>();
  }

  return new Set(ancestorIds);
}
